import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Button,
  Link,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Help as HelpIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as ShoppingIcon,
  HealthAndSafety as HealthIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const FAQ = () => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const categories = [
    {
      title: "Aplikace",
      icon: <SearchIcon sx={{ color: '#ff6b6b' }} />,
      questions: [
        {
          q: "Jak přesné jsou výsledky skenování?",
          a: "Naše aplikace využívá rozsáhlou databázi produktů s ověřenými informacemi od výrobců. " +
             "Přesnost je velmi vysoká, ale vždy doporučujeme zkontrolovat také etiketu produktu, " +
             "zejména pokud jste velmi citliví na lepek."
        },
        {
          q: "Co dělat, když produkt není v databázi?",
          a: "Pokud produkt není v databázi, doporučujeme: 1) Pečlivě přečíst etiketu, " +
             "2) Kontaktovat výrobce pro ověření, 3) Nahlásit nám chybějící produkt přes kontaktní formulář."
        },
        {
          q: "Funguje aplikace offline?",
          a: "Pro skenování a vyhledávání v databázi je potřeba internetové připojení. " +
             "Pracujeme na funkci offline seznamu oblíbených produktů."
        }
      ]
    },
    {
      title: "Bezlepková dieta",
      icon: <RestaurantIcon sx={{ color: '#ff6b6b' }} />,
      questions: [
        {
          q: "Co je to lepek a kde se nachází?",
          a: "Lepek (gluten) je bílkovina nacházející se v pšenici, žitu, ječmeni a někdy v ovsu. " +
             "Je přítomen v běžném pečivu, těstovinách, ale může se skrývat i v méně očekávaných " +
             "potravinách jako jsou omáčky, polévky nebo zpracované maso."
        },
        {
          q: "Jaké jsou bezlepkové alternativy?",
          a: "Existuje mnoho bezlepkových alternativ včetně: rýže, kukuřice, quinoy, pohanky, " +
             "amarantu a mnoha dalších. Moderní trh nabízí také širokou škálu bezlepkových verzí " +
             "běžných potravin jako je pečivo, těstoviny nebo sušenky."
        }
      ]
    },
    {
      title: "Nakupování",
      icon: <ShoppingIcon sx={{ color: '#ff6b6b' }} />,
      questions: [
        {
          q: "Jak poznám bezlepkové potraviny?",
          a: "Bezlepkové potraviny poznáte podle: 1) Symbolu přeškrtnutého klasu, " +
             "2) Označení 'bez lepku' nebo 'gluten-free', 3) Složení neobsahující lepkové obiloviny. " +
             "Vždy je důležité číst etikety, i u známých produktů."
        },
        {
          q: "Kde nakupovat bezlepkové potraviny?",
          a: "Bezlepkové potraviny najdete v: 1) Specializovaných prodejnách zdravé výživy, " +
             "2) Většině supermarketů v sekci bezlepkových potravin, 3) Online obchodech " +
             "specializovaných na bezlepkové produkty."
        }
      ]
    },
    {
      title: "Zdraví",
      icon: <HealthIcon sx={{ color: '#ff6b6b' }} />,
      questions: [
        {
          q: "Jak poznám, že mám celiakii?",
          a: "Celiakii může diagnostikovat pouze lékař na základě krevních testů a biopsie tenkého " +
             "střeva. Mezi typické příznaky patří: průjem, únava, nadýmání, úbytek váhy, ale příznaky " +
             "se mohou u každého lišit."
        },
        {
          q: "Je bezlepková dieta zdravá pro každého?",
          a: "Bezlepková dieta je nezbytná pro osoby s celiakií nebo citlivostí na lepek. Pro ostatní " +
             "není nutná a může vést k nedostatku některých živin, pokud není správně vyvážená. Vždy " +
             "konzultujte změny stravy s lékařem."
        }
      ]
    }
  ];

  return (
    <Box>
      {/* FAQ Header */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Často kladené otázky
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, textAlign: 'center' }}>
          Odpovědi na nejčastější otázky o bezlepkové dietě a používání naší aplikace.
          Nenašli jste odpověď? <Link component={RouterLink} to="/contact" sx={{ color: '#ff6b6b' }}>Kontaktujte nás</Link>.
        </Typography>
      </Paper>

      {/* Category Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {categories.map((category) => (
          <Grid item xs={12} sm={6} md={3} key={category.title}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {category.icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {category.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {category.questions.length} otázek
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* FAQ Sections */}
      {categories.map((category) => (
        <Paper key={category.title} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {category.icon}
            <Typography variant="h5" sx={{ ml: 2, color: '#ff6b6b', fontWeight: 'bold' }}>
              {category.title}
            </Typography>
          </Box>
          
          {category.questions.map((qa, index) => (
            <Accordion
              key={index}
              expanded={expanded === `${category.title}-${index}`}
              onChange={handleChange(`${category.title}-${index}`)}
              sx={{
                mb: 1,
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '8px !important',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '&.Mui-expanded': {
                    minHeight: 48,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>{qa.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'text.secondary' }}>{qa.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      ))}

      {/* Help Box */}
      <Paper sx={{ p: 4, borderRadius: 3, bgcolor: '#fff3e0', textAlign: 'center' }}>
        <HelpIcon sx={{ fontSize: 40, color: '#ff6b6b', mb: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ color: '#e65100', fontWeight: 'bold' }}>
          Potřebujete další pomoc?
        </Typography>
        <Typography paragraph sx={{ mb: 3 }}>
          Nenašli jste odpověď na svou otázku? Rádi vám pomůžeme.
        </Typography>
        <Button
          component={RouterLink}
          to="/contact"
          variant="contained"
          sx={{
            bgcolor: '#ff6b6b',
            '&:hover': { bgcolor: '#ff5252' },
            borderRadius: 50,
            px: 4,
          }}
        >
          Kontaktujte nás
        </Button>
      </Paper>
    </Box>
  );
};

export default FAQ;
